import { Card } from "primereact/card";
import { SafeReportResponse } from "../Conversion";
import styles from "./DeductibleCard.module.css";
import { TabPanel, TabView } from "primereact/tabview";
import * as client from "../../../client";
import { useContext, useState } from "react";
import { cloneDeep } from "lodash";
import { ThemeContext } from "../../../themes/Theme";
import { Button } from "primereact/button";
import { testDeductibleOrLimitFunction } from "../../../api/routes";
import { useToast } from "../../../context-providers/Toast";
import { readableNameFromPeril } from "../general-input/util";
import { useUpdateReport } from "../data";
import { EditableFunctionBlock } from "../EditableFunctionBlock";

interface DeductibleCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

export const DeductibleCard: React.FC<DeductibleCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const deductibleInfo = specialtyPropertyInfo.report_json.deductible_info;
  const extractedDeductibles = deductibleInfo?.extracted_deductibles ?? {};
  const manualDeductibles = deductibleInfo?.manual_deductibles ?? {};
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const toast = useToast();
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);

  const filenames = Object.keys(extractedDeductibles);
  const selectedFormName =
    specialtyPropertyInfo.report_json.policy_info?.selected_policy_form_name;
  const initialIndex =
    selectedFormName && filenames.includes(selectedFormName)
      ? filenames.indexOf(selectedFormName)
      : 0;
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  // Persist (or clear, when override is null) the hand-edited function for one
  // peril. Clones only the deductible_info subtree and sends it back whole; the
  // generated executable_python_function_str is left untouched. For a manual
  // override, stamp the form it's authored against so cat modeling only blocks
  // once the report later switches forms (see report_has_stale_override).
  const applyOverride = async (
    filename: string | null,
    peril: client.peril_type,
    override: string | null
  ): Promise<void> => {
    if (!deductibleInfo) return;
    const next = cloneDeep(deductibleInfo);
    const entry =
      filename === null
        ? next.manual_deductibles[peril]
        : next.extracted_deductibles[filename]?.[peril];
    if (!entry) return;
    entry.executable_python_function_override = override;
    // Only manual overrides (filename === null) can go stale — extracted ones are
    // scoped to their form's bucket — so only they carry the authored-form stamp.
    // Clearing the override clears the stamp.
    if (filename === null) {
      entry.override_authored_against_form =
        override === null ? null : (selectedFormName ?? null);
    }
    const readablePerilName = readableNameFromPeril(peril);
    try {
      await updateReport.mutateAsync({ deductibleInfo: next });
      toast.show({
        severity: "success",
        summary:
          override === null
            ? "Reverted to Generated Function"
            : "Deductible Function Saved",
        detail:
          override === null
            ? `The ${readablePerilName} function was reverted to the generated version.`
            : `The ${readablePerilName} function override was saved.`,
      });
    } catch (e) {
      toast.show({
        severity: "error",
        summary: "Save Failed",
        detail: `Could not update the ${readablePerilName} function.`,
      });
      throw e;
    }
  };

  const renderPeril = (
    filename: string | null,
    peril: string,
    advancedDeductible: client.AdvancedDeductible
  ) => {
    const perilType = peril as client.peril_type;
    const readablePerilName = readableNameFromPeril(perilType);
    const effectiveFunction =
      advancedDeductible.executable_python_function_override ??
      advancedDeductible.executable_python_function_str;
    const hasOverride =
      advancedDeductible.executable_python_function_override != null;
    const hasFunction = effectiveFunction !== "";

    if (!hasFunction) {
      return (
        <div className={styles.verticalContainerSmall} key={peril}>
          <div className={styles.horizontalContainer}>
            <span className={styles.header}>{readablePerilName}</span>
          </div>
          <span style={{ fontSize: "14px" }}>
            {advancedDeductible.short_deductible_description}
          </span>
          <span
            style={{
              fontSize: "13px",
              fontStyle: "italic",
              color: "var(--text-color-secondary)",
            }}
          >
            Function will be generated on next rerun.
          </span>
        </div>
      );
    }

    return (
      <EditableFunctionBlock
        key={peril}
        readablePerilName={readablePerilName}
        description={advancedDeductible.short_deductible_description}
        testButton={
          <Button
            label="Test"
            outlined
            size="small"
            style={{ padding: "4px 6px" }}
            onClick={async () => {
              try {
                const result = await testDeductibleOrLimitFunction(
                  specialtyPropertyInfo.report_id,
                  filename,
                  perilType,
                  "deductible"
                );
                toast.show({
                  severity: "success",
                  summary: "Deductible Function Success",
                  detail: `The ${readablePerilName} function ran successfully on ${result.num_addresses_run} addresses.`,
                });
              } catch {
                toast.show({
                  severity: "error",
                  summary: "Deductible Function Failure",
                  detail: `Something went wrong when running the ${readablePerilName} function.`,
                });
              }
            }}
          />
        }
        functionStr={effectiveFunction}
        hasOverride={hasOverride}
        isLightMode={isLightMode}
        isSaving={updateReport.isPending}
        onSave={(nextOverride) =>
          applyOverride(filename, perilType, nextOverride)
        }
        onRevert={() => applyOverride(filename, perilType, null)}
      />
    );
  };

  return (
    <Card title="Deductible">
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
      >
        {Object.entries(extractedDeductibles).map(
          ([filename, extractedDeductiblesForFile]) => {
            return (
              <TabPanel header={filename} key={filename}>
                <div className={styles.verticalContainer}>
                  {Object.entries(extractedDeductiblesForFile).map(
                    ([peril, advancedDeductible]) =>
                      renderPeril(filename, peril, advancedDeductible)
                  )}
                </div>
              </TabPanel>
            );
          }
        )}
        {Object.keys(manualDeductibles).length > 0 && (
          <TabPanel header="Manual">
            <div className={styles.verticalContainer}>
              {Object.entries(manualDeductibles).map(
                ([peril, advancedDeductible]) =>
                  renderPeril(null, peril, advancedDeductible)
              )}
            </div>
          </TabPanel>
        )}
      </TabView>
    </Card>
  );
};
